/**
 * Identidad y contacto configurables (Admin → Sitio público → «Identidad y contacto»): única fuente de los
 * datos del responsable en el sitio. Paquete legal Pizas v2026-10-09 (diseño portado de DemoCitas).
 *
 * Fuente: GET /api/public/legal-identity (backend `legal_identity.py`, colección `legal_identity`).
 * Las condiciones comerciales ({{costo_envio}}, {{vigencia_promos}}…) llegan en GET /api/menu → store.legal_terms
 * y el {{horario}} sale de GET /api/schedule. Un dato vacío se muestra como «dato pendiente» (nunca un valor
 * heredado ni el marcador crudo). Enlaces a páginas propias: {{page:/ruta}} → ruta RELATIVA del mismo dominio.
 */
import { useEffect, useState } from 'react';
import { getPublicLegalIdentity, getPublicMenu } from './api';

export const LEGAL_PENDING_LABEL = 'dato pendiente';
/** Texto de tribunales cuando el campo está vacío (ajuste de Mi Lic; espejo de legal_identity.py). */
export const DEFAULT_JUDICIAL_DISTRICT =
  'los tribunales competentes del domicilio del proveedor, sin perjuicio de la competencia de Profeco';
export const CURRENT_NOTICE_VERSION = 'v2026-10-09';

/** Campos editables en el panel (espejo de legal_identity.py; `required` = bloquea «publicable»). */
export const LEGAL_IDENTITY_FIELDS = [
  { key: 'responsible_name', label: 'Nombre del responsable (persona física o razón social)', required: true },
  { key: 'trade_name', label: 'Nombre comercial (proveedor)', required: true },
  { key: 'rfc', label: 'RFC del responsable', required: true },
  { key: 'address', label: 'Domicilio del responsable', required: true },
  { key: 'city', label: 'Ciudad / estado', required: false },
  { key: 'privacy_email', label: 'Correo de privacidad y derechos ARCO (también el de contacto)', required: true },
  { key: 'phone', label: 'Teléfono (también cabecera, pie y /contacto)', required: true },
  { key: 'whatsapp', label: 'WhatsApp de pedidos (vacío = el teléfono)', required: false },
  { key: 'website', label: 'Sitio web (dominio publicado)', required: true },
  { key: 'store_brand', label: 'Marca de la tienda (pizzería de ejemplo)', required: true },
  { key: 'pickup_address', label: 'Domicilio para recoger (vacío = domicilio del responsable)', required: false },
  {
    key: 'judicial_district',
    label: 'Tribunales competentes (frase completa; vacío = texto por defecto)',
    required: false,
    hint: 'Se lee «las partes se someten a …». Vacío = «los tribunales competentes del domicilio del proveedor, sin perjuicio de la competencia de Profeco».',
  },
  {
    key: 'hosting_country',
    label: 'País de los servidores de alojamiento (opcional)',
    required: false,
    placeholder: 'p. ej. Estados Unidos',
    hint: 'Si lo llenas, el aviso agrega «con servidores ubicados en …»; vacío no bloquea publicar.',
  },
  { key: 'wa_ai_provider', label: 'Proveedor de IA del asistente de WhatsApp (obligatorio si la IA está activa)', required: false },
  { key: 'demo_purge_days', label: 'Plazo de borrado de pedidos de prueba', required: false },
  { key: 'last_updated', label: 'Fecha de última actualización de los textos', required: true },
  { key: 'notice_version', label: 'Versión vigente del aviso', required: true },
];

const LABELS = Object.fromEntries(LEGAL_IDENTITY_FIELDS.map((f) => [f.key, f.label]));

/** {{token}} del paquete de Mi Lic → campo de identidad. */
const IDENTITY_TOKENS = {
  responsable_nombre: 'responsible_name',
  nombre_comercial: 'trade_name',
  rfc: 'rfc',
  domicilio: 'address',
  ciudad_estado: 'city',
  correo_privacidad: 'privacy_email',
  telefono: 'phone',
  whatsapp: 'whatsapp',
  sitio: 'website',
  marca_tienda: 'store_brand',
  domicilio_recoger: 'pickup_address',
  distrito_judicial: 'judicial_district',
  pais_servidores: 'hosting_country',
  ia_whatsapp: 'wa_ai_provider',
  plazo_borrado_demo: 'demo_purge_days',
  fecha_actualizacion: 'last_updated',
  version_aviso: 'notice_version',
};

/** {{token}} → clave de store.legal_terms (GET /api/menu). */
export const TERMS_TOKENS = {
  radio_entrega_km: 'Radio de entrega (Config tienda)',
  costo_envio: 'Costo de envío (Config tienda)',
  pedido_minimo: 'Pedido mínimo',
  tiempo_entrega: 'Tiempo estimado de entrega (Config tienda)',
  iva_incluido: 'Precios con IVA incluido',
  factura: 'Texto de factura',
  vigencia_promos: 'Vigencia de las promociones',
  descuento_calificar: 'Descuento por calificar',
  condiciones_descuento_calificar: 'Condiciones del descuento por calificar',
  plazo_reclamo: 'Plazo sugerido para reclamar',
  plazo_reembolso: 'Plazo de reembolso',
  medios_contra_entrega: 'Medios aceptados contra entrega',
};

export function legalValue(identity, key) {
  const v = String(identity?.[key] ?? '').trim();
  if (v) return v;
  if (key === 'whatsapp') return String(identity?.phone ?? '').trim();
  if (key === 'pickup_address') return String(identity?.address ?? '').trim();
  if (key === 'judicial_district') return DEFAULT_JUDICIAL_DISTRICT;
  return '';
}

export function missingRequiredLegalFields(identity) {
  if (Array.isArray(identity?.missing_required)) return identity.missing_required;
  return LEGAL_IDENTITY_FIELDS.filter((f) => f.required && !legalValue(identity, f.key)).map((f) => f.key);
}

export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** `tel:+528112141456` a partir de `81 1214 1456` (10 dígitos = México). */
export function telHref(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 10) return '';
  if (digits.length === 10) return `tel:+52${digits}`;
  return `tel:+${digits}`;
}

/** `https://wa.me/528112141456`. */
export function waHref(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 10) return '';
  return `https://wa.me/${digits.length === 10 ? `52${digits}` : digits}`;
}

export function mailtoHref(email) {
  const e = String(email || '').trim();
  return /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/.test(e) ? `mailto:${e}` : '';
}

export function websiteLabel(website) {
  return String(website || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

function pendingHtml(key, label) {
  return `<mark class="legal-pending" data-legal-pending="${escapeHtml(key)}" title="${escapeHtml(
    `${label || LABELS[key] || key}: ${LEGAL_PENDING_LABEL}`,
  )}">${LEGAL_PENDING_LABEL}</mark>`;
}

function linkHtml(href, text, external = false) {
  if (!href) return escapeHtml(text);
  const ext = external ? ' target="_blank" rel="noopener noreferrer"' : '';
  return `<a href="${escapeHtml(href)}"${ext}>${escapeHtml(text)}</a>`;
}

/** Enlace RELATIVO a una página del propio sitio; el texto muestra dominio + ruta. */
function pageHtml(path, identity) {
  const p = /^\/[\w/-]*$/.test(path || '') ? path : '/';
  const host =
    websiteLabel(legalValue(identity, 'website')) ||
    (typeof window !== 'undefined' ? window.location.host : '');
  return `<a href="${escapeHtml(p)}">${escapeHtml(p === '/' ? host : `${host}${p}`)}</a>`;
}

function identityHtml(identity, token) {
  const key = IDENTITY_TOKENS[token];
  const v = legalValue(identity, key);
  if (!v) return pendingHtml(key);
  if (key === 'privacy_email') return linkHtml(mailtoHref(v), v);
  if (key === 'phone') return linkHtml(telHref(v), v);
  if (key === 'whatsapp') return linkHtml(waHref(v), v, true);
  if (key === 'website') return escapeHtml(websiteLabel(v));
  return escapeHtml(v);
}

function conditionTrue(cond, ctx) {
  if (cond === 'ia') return Boolean(ctx.identity?.wa_ai_enabled);
  if (cond in LABELS) return Boolean(legalValue(ctx.identity, cond));
  return Boolean(String(ctx.terms?.[cond] ?? '').trim());
}

/** <!--if:x-->sí<!--else-->no<!--/if--> (el «else» es opcional). */
const IF_RE = /<!--if:([a-z_]+)-->([\s\S]*?)<!--\/if-->/g;
const TOKEN_RE = /\{\{([a-z_]+)(?::([^}]*))?\}\}/g;

/**
 * Rellena un texto legal. ctx = { identity, terms (store.legal_terms), schedule (líneas de horario) }.
 * Todo valor se escapa; los bloques condicionales (IA, medios contra entrega, descuento por calificar)
 * se quitan si no aplican.
 */
export function renderLegalHtml(html, ctx = {}) {
  const identity = ctx.identity || {};
  const terms = ctx.terms || {};
  return String(html || '')
    .replace(IF_RE, (_m, cond, inner) => {
      const [yes, no = ''] = inner.split('<!--else-->');
      return conditionTrue(cond, ctx) ? yes : no;
    })
    .replace(TOKEN_RE, (_m, token, arg) => {
      if (token === 'page') return pageHtml(arg || '/', identity);
      if (token === 'site') return pageHtml('/', identity);
      if (token === 'anio') return String(new Date().getFullYear());
      if (token === 'horario') {
        const h = String(ctx.horario || '').trim();
        return h ? escapeHtml(h) : pendingHtml('horario', 'Horario (panel «Horarios»)');
      }
      if (token in IDENTITY_TOKENS) return identityHtml(identity, token);
      if (token in TERMS_TOKENS) {
        const v = String(terms[token] ?? '').trim();
        return v ? escapeHtml(v) : pendingHtml(token, TERMS_TOKENS[token]);
      }
      return '';
    });
}

/** Igual que renderLegalHtml, pero para textos cortos sin HTML (pie, banners): devuelve texto plano. */
export function renderLegalText(text, ctx = {}) {
  const identity = ctx.identity || {};
  const terms = ctx.terms || {};
  return String(text || '').replace(TOKEN_RE, (_m, token) => {
    if (token === 'anio') return String(new Date().getFullYear());
    if (token === 'horario') return String(ctx.horario || '').trim() || LEGAL_PENDING_LABEL;
    if (token in IDENTITY_TOKENS) {
      const key = IDENTITY_TOKENS[token];
      const v = legalValue(identity, key);
      return key === 'website' ? websiteLabel(v) || LEGAL_PENDING_LABEL : v || LEGAL_PENDING_LABEL;
    }
    if (token in TERMS_TOKENS) return String(terms[token] ?? '').trim() || LEGAL_PENDING_LABEL;
    return '';
  });
}

let cache = null;
let inflight = null;

export function fetchLegalIdentity({ force = false } = {}) {
  if (cache && !force) return Promise.resolve(cache);
  if (!inflight || force) {
    inflight = getPublicLegalIdentity()
      .then((data) => {
        cache = data && typeof data === 'object' ? data : {};
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function invalidateLegalIdentity() {
  cache = null;
}

/** { identity, loading, error }. Si falla la API, identity = {} → «dato pendiente» (nunca datos heredados). */
export function useLegalIdentity() {
  const [state, setState] = useState(() => ({ identity: cache || {}, loading: !cache, error: null }));
  useEffect(() => {
    let alive = true;
    if (cache) return undefined;
    fetchLegalIdentity()
      .then((identity) => alive && setState({ identity, loading: false, error: null }))
      .catch((error) => alive && setState({ identity: {}, loading: false, error }));
    return () => {
      alive = false;
    };
  }, []);
  return state;
}

let termsCache = null;
let termsInflight = null;

/** store.legal_terms de GET /api/menu (condiciones comerciales ya formateadas por el backend). */
export function useLegalTerms(initial = null) {
  const [terms, setTerms] = useState(() => initial || termsCache || {});
  useEffect(() => {
    if (initial) {
      termsCache = initial;
      return undefined;
    }
    if (termsCache) return undefined;
    let alive = true;
    if (!termsInflight) {
      termsInflight = getPublicMenu()
        .then((m) => {
          termsCache = (m && m.store && m.store.legal_terms) || {};
          return termsCache;
        })
        .catch(() => ({}))
        .finally(() => {
          termsInflight = null;
        });
    }
    termsInflight.then((t) => alive && setTerms(t || {}));
    return () => {
      alive = false;
    };
  }, [initial]);
  return terms;
}
